"use client";
import type { LanguageOrAccent } from "@components/languages/config";

import { PillGrid } from "@components/composers/PillGrid";
import { FlagPill } from "@components/languages/FlagPill";
import { asrLanguages, ttsLanguages } from "@components/languages/config";
import { Button } from "@ui/components/core/Button";
import DynamicFlag from "@ui/components/languages/DynamicFlag";
import { useTranslations } from "next-intl";
import { useState } from "react";

const languageToPill = (language: LanguageOrAccent, showTTS: boolean) => ({
  language,
  href: showTTS ? `/text-to-speech/${language.slug}` : `/speech-to-text/${language.slug}`,
});

export const LanguagesPillGrid = ({
  title,
  description,
  narrativeBlock = true,
  showTTS = true,
  languagesOverride,
}: {
  description?: string;
  languagesOverride?: LanguageOrAccent[];
  narrativeBlock?: boolean;
  showTTS?: boolean;
  title?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const expandable = !!languagesOverride?.length;
  const t = useTranslations("ClientKeys.languages_pill_grid");

  const allLanguages = (showTTS ? ttsLanguages : asrLanguages).map(language => languageToPill(language, showTTS));
  const subsetLanguages = !expandable
    ? allLanguages
    : languagesOverride?.map(language => languageToPill(language, showTTS));

  const languagesToShow = !expandable || isExpanded ? allLanguages : subsetLanguages;

  return (
    <>
      <PillGrid description={description} title={title}>
        {languagesToShow.map(({ language, href }, index) => (
          <FlagPill
            flag={
              <DynamicFlag
                alt={t(showTTS ? "tts_flag_alt" : "asr_flag_alt", { language: language.label })}
                className='rounded-full'
                locale={language.value}
                size={24}
              />
            }
            hideArrow={narrativeBlock}
            href={href}
            key={index}
            name={language.label}
          />
        ))}
      </PillGrid>
      {expandable && (
        <Button onClick={() => setIsExpanded(!isExpanded)} variant='secondary'>
          {isExpanded ? t("view_less_languages") : t("view_all_languages")}
        </Button>
      )}
    </>
  );
};
export default LanguagesPillGrid;
